import { Component, OnInit, Inject, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { EquipmentModelService, BrandService } from '@app/api/api';
import { EquipmentTypeService } from '@app/api/equipmentType.service';
import { EquipmentCategoryService } from '@app/api/equipmentCategory.service';
import { ToastrService } from 'ngx-toastr';

export interface Brand {
  id: string | number;
  name: string;
  code?: string;
}

@Component({
  selector: 'meu-modal-equipment-model',
  templateUrl: './modal-equipment-model.component.html',
  styleUrls: ['./modal-equipment-model.component.scss'],
})
export class ModalEquipmentModelComponent implements OnInit {
  isEdit: boolean = false;
  hasFormErrors: boolean = false;
  lstBrands: Brand[] = [];
  lstEquipmentCategories: any[] = [];
  lstEquipmentTypes: any[] = [];

  modelForm = new FormGroup({
    brand_id: new FormControl('', [Validators.required]),
    model_code: new FormControl('', [Validators.required, Validators.pattern('^(?!\\s*$).+')]),
    equipment_category_id: new FormControl('', [Validators.required]),
    equipment_type_id: new FormControl('', [Validators.required]),
    description: new FormControl(''),
    sort_order: new FormControl(0),
    engine_type: new FormControl(''),
    transmission_type: new FormControl(''),
    mast_type: new FormControl(''),
  });
  existingPrefixes: any[] = [];
  prefixTags: { id?: string; prefix_code: string }[] = [];
  prefixInput = '';
  prefixTouched = false;

  constructor(
    public dialogRef: MatDialogRef<ModalEquipmentModelComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { id: string | number | null },
    private equipmentModelService: EquipmentModelService,
    private brandService: BrandService,
    private equipmentTypeService: EquipmentTypeService,
    private equipmentCategoryService: EquipmentCategoryService,
    private toastr: ToastrService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadBrands();
    this.loadEquipmentCategories();

    const categoryCtrl = this.modelForm.get('equipment_category_id');
    if (categoryCtrl) {
      categoryCtrl.valueChanges.subscribe((categoryId) => {
        this.modelForm.patchValue({ equipment_type_id: '' }, { emitEvent: false });
        this.loadEquipmentTypesByCategory(categoryId || undefined);
      });
    }

    if (this.data.id != null) {
      this.isEdit = true;
      this.getById(this.data.id);
    }
  }

  addPrefixTag() {
    const raw = this.prefixInput.trim();
    if (!raw) return;
    const codes = raw.split(',').map((c) => c.trim()).filter((c) => c);
    for (const code of codes) {
      if (!this.prefixTags.some((p) => p.prefix_code.toLowerCase() === code.toLowerCase())) {
        this.prefixTags.push({ prefix_code: code });
      }
    }
    this.prefixInput = '';
    this.changeDetectorRef.detectChanges();
  }

  onPrefixInputKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addPrefixTag();
    } else if (event.key === 'Backspace' && !this.prefixInput && this.prefixTags.length > 0) {
      this.removePrefixTag(this.prefixTags.length - 1);
    }
  }

  removePrefixTag(index: number) {
    this.prefixTags.splice(index, 1);
    this.changeDetectorRef.detectChanges();
  }

  onPrefixInputBlur() {
    if (this.prefixInput.trim()) {
      this.addPrefixTag();
    }
  }

  loadBrands() {
    this.brandService.apiBrandsGet('', '', 1, 1000).subscribe((res) => {
      if (res.success) {
        this.lstBrands = res.data.collection;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  loadEquipmentCategories() {
    this.equipmentCategoryService.apiEquipmentCategoriesGet('', '', 1, 1000).subscribe((res) => {
      if (res.success) {
        this.lstEquipmentCategories = res.data.collection;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  loadEquipmentTypesByCategory(categoryId?: string, selectedTypeId?: string, extendType?: any) {
    if (!categoryId) {
      this.lstEquipmentTypes = [];
      this.changeDetectorRef.detectChanges();
      return;
    }

    const filters = 'category_id==' + categoryId;
    this.equipmentTypeService.apiEquipmentTypesGet(filters, '', 1, 1000).subscribe((res) => {
      if (res.success) {
        this.lstEquipmentTypes = res.data.collection;
        if (extendType && selectedTypeId) {
          const typeExists = this.lstEquipmentTypes.some((t) => t.id === selectedTypeId);
          if (!typeExists) {
            this.lstEquipmentTypes.push(extendType);
          }
        }
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  getById(id: string | number) {
    this.equipmentModelService.apiEquipmentModelsIdGet(String(id)).subscribe((res) => {
      if (res.success) {
        const data = res.data;

        if (data.extend_brand) {
          const brandExists = this.lstBrands.some((b) => b.id === data.brand_id);
          if (!brandExists) {
            this.lstBrands.push(data.extend_brand);
          }
        }
        if (data.extend_equipment_category) {
          const catExists = this.lstEquipmentCategories.some((c) => c.id === data.equipment_category_id);
          if (!catExists) {
            this.lstEquipmentCategories.push(data.extend_equipment_category);
          }
        }

        this.modelForm.patchValue({
          brand_id: data.brand_id,
          model_code: data.model_code,
          equipment_category_id: data.equipment_category_id || '',
          equipment_type_id: data.equipment_type_id || '',
          description: data.description,
          sort_order: data.sort_order || 0,
          engine_type: data.engine_type || '',
          transmission_type: data.transmission_type || '',
          mast_type: data.mast_type || '',
        }, { emitEvent: false });

        const categoryId = data.equipment_category_id;
        if (categoryId) {
          this.loadEquipmentTypesByCategory(categoryId, data.equipment_type_id, data.extend_equipment_type);
        }

        this.existingPrefixes = data.prefixes || [];
        this.prefixTags = this.existingPrefixes.map((p: any) => ({
          id: p.id,
          prefix_code: p.prefix_code || ''
        }));

        this.changeDetectorRef.detectChanges();
      }
    });
  }

  save() {
    this.hasFormErrors = true;
    const controls = this.modelForm.controls;
    if (this.modelForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      return;
    }

    const payload = this.modelForm.value;
    payload['is_active'] = true;

    const prefixes = this.prefixTags
      .filter((p) => p.prefix_code && p.prefix_code.trim())
      .map((p) => ({ id: p.id, prefix_code: p.prefix_code.trim() }));

    if (!this.isEdit) {
      payload['prefixes'] = prefixes.map((p) => ({ prefix_code: p.prefix_code }));
      this.equipmentModelService.apiEquipmentModelsPost(payload).subscribe((res) => {
        if (res.success) {
          this.dialogRef.close({ event: true, type: 'create' });
        } else {
          this.dialogRef.close({ event: false, message: res.message });
        }
      });
    } else {
      payload['prefixes'] = prefixes;
      this.equipmentModelService.apiEquipmentModelsIdPut(String(this.data.id), payload).subscribe((res) => {
        if (res.success) {
          this.dialogRef.close({ event: true, type: 'edit' });
        } else {
          this.dialogRef.close({ event: false, message: res.message });
        }
      });
    }
  }

  closeModal() {
    this.dialogRef.close();
  }
}
